import * as THREE from 'three';
import { creatures, type CreatureDef } from '../data/creatures';
import { lambert } from '../world/Assets';

/**
 * Canlı varlığı (hayvan / düşman / binek).
 * Davranış durum makinesi veriden beslenir. Gövde/kafa/4 bacak AYRI mesh'ler
 * olarak tek bir Group'ta tutulur (geometri hâlâ tür başına paylaşılır, draw
 * call sayısı artmaz — sadece transform'lar canlı başına farklıdır) ki
 * yürüyüş/duruş/saldırı prosedürel olarak (keyframe'siz, sin/cos tabanlı)
 * animasyon uygulanabilsin.
 */

export type CreatureState = 'idle' | 'wander' | 'flee' | 'chase' | 'attack' | 'dead';

const bodyGeoCache = new Map<string, THREE.BufferGeometry>();
const headGeoCache = new Map<string, THREE.BufferGeometry>();
const legGeoCache = new Map<string, THREE.BufferGeometry>();

function bodyGeometry(def: CreatureDef): THREE.BufferGeometry {
  const cached = bodyGeoCache.get(def.id);
  if (cached) return cached;
  const s = def.size;
  const geo = new THREE.BoxGeometry(s * 0.62, s * 0.52, s);
  geo.computeVertexNormals();
  bodyGeoCache.set(def.id, geo);
  return geo;
}

function headGeometry(def: CreatureDef): THREE.BufferGeometry {
  const cached = headGeoCache.get(def.id);
  if (cached) return cached;
  const s = def.size;
  const geo = new THREE.BoxGeometry(s * 0.42, s * 0.4, s * 0.4);
  geo.computeVertexNormals();
  headGeoCache.set(def.id, geo);
  return geo;
}

function legGeometry(def: CreatureDef): THREE.BufferGeometry {
  const cached = legGeoCache.get(def.id);
  if (cached) return cached;
  const s = def.size;
  const geo = new THREE.BoxGeometry(s * 0.14, s * 0.3, s * 0.14);
  // Pivot ayağın altına değil, uyluğun (bacağın üst) hizasına konur ki
  // bacak sallanırken yer altına batmasın: pivotu üstte, geometriyi aşağı kaydır.
  geo.translate(0, -s * 0.15, 0);
  geo.computeVertexNormals();
  legGeoCache.set(def.id, geo);
  return geo;
}

/** Bacak takımı: 4 bacağın yürüyüş fazındaki (radyan) ofseti — çapraz yürüyüş (trot). */
const LEG_PHASE = [0, Math.PI, Math.PI, 0];
const WHITE = new THREE.Color(0xffffff);

export class Creature {
  readonly def: CreatureDef;
  /** Kök Group: konum/yaw buraya uygulanır, dış kod (CreatureManager) bunu Mesh gibi kullanır. */
  readonly mesh: THREE.Group;
  private readonly body: THREE.Mesh;
  private readonly head: THREE.Mesh;
  private readonly legs: THREE.Mesh[];
  health: number;
  state: CreatureState = 'idle';
  stateTimer = 0;
  yaw = 0;
  /** Saldırı bekleme sayacı. */
  cooldown = 0;
  /** Ölüm animasyonu için kalan süre. */
  deathTimer = 0;
  /** Uzaktaki canlılar güncellenmez (performans). */
  frozen = false;
  readonly position = new THREE.Vector3();
  /** Prosedürel yürüyüş fazı (saniye biriktirilir, hıza göre ilerler). */
  private gaitPhase = 0;
  /** Saldırı lunge animasyonu ilerlemesi (0..1, her gerçek saldırıda yeniden başlar). */
  private attackAnim = 0;
  private attacking = false;
  /** Bu canlıya özel klonlanmış materyal (vuruş flaşı için). */
  private readonly material: THREE.MeshLambertMaterial;
  /** Vuruş beyaz flaşı: 0 = normal renk, >0 = beyaza karışım oranı, sönümlenir. */
  private hitFlash = 0;
  private readonly baseColor = new THREE.Color();

  constructor(readonly id: string, defId: string, x: number, y: number, z: number, healthMul = 1) {
    const def = creatures.get(defId) ?? creatures.require('rabbit');
    this.def = def;
    this.health = Math.max(1, Math.round(def.health * healthMul));
    this.position.set(x, y, z);

    const s = def.size;
    // Materyal PAYLAŞILMIYOR (lambert() cache'i tür başına tek materyal döner):
    // vuruş anında bu canlıya özel beyaz "flash" uygulanacağı için klonlanır.
    // Klon ucuzdur (geometri hâlâ paylaşılıyor, sadece küçük bir materyal nesnesi).
    const mat = (lambert(def.color) as THREE.MeshLambertMaterial).clone();
    this.material = mat;
    this.mesh = new THREE.Group();

    this.body = new THREE.Mesh(bodyGeometry(def), mat);
    this.body.position.y = s * 0.52;
    this.body.castShadow = false;
    this.body.frustumCulled = true;
    this.mesh.add(this.body);

    this.head = new THREE.Mesh(headGeometry(def), mat);
    this.head.position.set(0, s * 0.72, s * 0.6);
    this.head.castShadow = false;
    this.head.frustumCulled = true;
    this.mesh.add(this.head);

    const legPositions: Array<[number, number]> = [
      [-s * 0.22, s * 0.3],
      [s * 0.22, s * 0.3],
      [-s * 0.22, -s * 0.3],
      [s * 0.22, -s * 0.3],
    ];
    this.legs = legPositions.map(([lx, lz]) => {
      const leg = new THREE.Mesh(legGeometry(def), mat);
      leg.position.set(lx, s * 0.3, lz);
      leg.castShadow = false;
      leg.frustumCulled = true;
      this.mesh.add(leg);
      return leg;
    });

    this.mesh.position.copy(this.position);
    this.yaw = Math.random() * Math.PI * 2;
    this.baseColor.copy(this.material.color);
  }

  /** Vurulunca çağrılır: gövde/kafa/bacaklar kısa süre beyaza yaklaşır. */
  flashHit(): void {
    this.hitFlash = 1;
  }

  /** Hasar zamanlamasıyla aynı anda saldırı telegraph/lunge başlatır. */
  beginAttack(): void {
    this.attackAnim = 0;
    this.attacking = true;
  }

  get alive(): boolean {
    return this.state !== 'dead';
  }

  get isHostile(): boolean {
    return this.def.behavior === 'aggressive';
  }

  /**
   * Görsel konum/yön/animasyonu uygular.
   * @param dt geçen süre (animasyon fazını ilerletmek için); verilmezse yalnızca
   *           statik konum/yaw uygulanır (ölüm animasyonu bunu kullanır).
   */
  syncMesh(dt = 0): void {
    this.mesh.position.copy(this.position);
    this.mesh.rotation.y = this.yaw;
    this.mesh.rotation.z = 0;
    this.mesh.rotation.x = 0;

    // Vuruş flaşı: hızla beyaza sıçrar, ~0.18s içinde normal rengine söner.
    if (this.hitFlash > 0) {
      this.hitFlash = Math.max(0, this.hitFlash - dt * 5.5);
      this.material.color.copy(this.baseColor).lerp(WHITE, this.hitFlash);
    }

    if (this.state === 'dead') {
      // Kan/parçalanma yok: yana devrilip yere gömülerek kaybolur
      this.mesh.rotation.z = Math.min(Math.PI / 2, (1 - this.deathTimer / 1.2) * (Math.PI / 2));
      this.mesh.position.y -= (1 - this.deathTimer / 1.2) * this.def.size * 0.35;
      return;
    }

    const s = this.def.size;
    const moving = this.state === 'wander' || this.state === 'chase' || this.state === 'flee';
    const speed = this.state === 'chase' || this.state === 'flee' ? this.def.runSpeed : this.def.moveSpeed;

    if (moving && speed > 0) {
      // Yürüyüş fazı gerçek hıza bağlı: hızlı canlı ayaklarını hızlı sallar.
      this.gaitPhase += dt * speed * 2.2;
    } else {
      // Dururken yürüyüş fazı sıfırlanır (bacaklar nötr pozisyona döner).
      this.gaitPhase += (0 - (this.gaitPhase % (Math.PI * 2))) * Math.min(1, dt * 8);
    }

    // Bacaklar dizden (üst pivot) öne/arkaya sallanır: çapraz (trot) yürüyüş.
    this.legs.forEach((leg, i) => {
      leg.rotation.x = moving ? Math.sin(this.gaitPhase + LEG_PHASE[i]!) * 0.55 : 0;
    });

    // Baş: hafif yukarı-aşağı sallanma (idle nefes alma), yürürken biraz artar.
    const bob = Math.sin(this.gaitPhase * (moving ? 1 : 0.35) + this.id.length) * (moving ? s * 0.02 : s * 0.015);
    this.head.position.y = s * 0.72 + bob;
    this.body.position.y = s * 0.52 + bob * 0.5;

    // Her gerçek saldırıda başlayan kısa lunge: cooldown boyunca attack state'inde
    // beklese bile donup görünmez olmaz.
    if (this.attacking) {
      this.attackAnim += dt * 5.5;
      if (this.attackAnim >= 1) { this.attackAnim = 1; this.attacking = false; }
    } else if (this.state === 'attack') {
      // Hasar cooldown'ı arasında da yırtıcının saldırmaya hazır olduğu okunur.
      this.attackAnim = (this.attackAnim + dt * 2.4) % 1;
    }
    if (this.attackAnim > 0) {
      const lunge = Math.sin(this.attackAnim * Math.PI) * s * 0.34;
      this.head.position.z = s * 0.6 + lunge;
      this.head.rotation.x = -Math.sin(this.attackAnim * Math.PI) * 0.42;
      this.body.position.z = lunge * 0.5;
    } else {
      this.head.position.z = s * 0.6;
      this.head.rotation.x = 0;
      this.body.position.z = 0;
    }
  }

  dispose(): void {
    // Geometri paylaşımlı, burada bırakılır. Materyal ise bu canlıya özel
    // klonlanmıştı (hit-flash için) — o serbest bırakılmalı.
    this.material.dispose();
    this.mesh.removeFromParent();
  }
}

export function creatureDef(id: string): CreatureDef | null {
  return creatures.get(id);
}
